import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeftIcon, EnvelopeSimpleIcon, KeyIcon, LockKeyIcon, CheckCircleIcon } from "@phosphor-icons/react";

/* ──────────────────────────────────────────────────────────────────────
 * 3-step password reset wizard:
 *   step 1 · email  → OTP mailed to user
 *   step 2 · otp    → verify OTP, receive short-lived reset_token
 *   step 3 · pw     → set new password
 * ────────────────────────────────────────────────────────────────────── */
export default function ForgotPassword() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [pw, setPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const requestOtp = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Enter your email");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      toast.success("OTP sent — check your inbox");
      setStep(2);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setBusy(false);
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (otp.trim().length < 4) return toast.error("Enter the OTP from your email");
    setBusy(true);
    try {
      const r = await api.post("/auth/verify-reset-otp", { email: email.trim(), otp: otp.trim() });
      setResetToken(r.data.reset_token);
      toast.success("OTP verified — set a new password");
      setStep(3);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setBusy(false);
  };

  const resendOtp = async () => {
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      toast.success("A new OTP has been sent");
      setOtp("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setBusy(false);
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== confirmPw) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { reset_token: resetToken, password: pw });
      toast.success("Password updated — please sign in");
      nav("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-brand-blue-tint">
      <div className="w-full max-w-md bg-white border border-slate-200 shadow-sm">
        {/* top strip */}
        <div className="px-8 pt-7 pb-5 border-b border-slate-100">
          <button
            type="button"
            onClick={() => nav("/login")}
            className="flex items-center text-xs uppercase tracking-wider text-slate-500 hover:text-brand-blue mb-4"
            data-testid="forgot-back-btn"
          >
            <ArrowLeftIcon size={12} className="mr-1" /> Back to login
          </button>
          <div className="overline text-brand-orange-ink mb-2">Account recovery</div>
          <h2 className="font-display text-2xl font-bold text-brand-blue">Reset your password</h2>
          <StepStrip step={step} />
        </div>

        {/* step bodies */}
        <div className="px-8 py-7">
          {step === 1 && (
            <form onSubmit={requestOtp} className="space-y-5" data-testid="forgot-step-email">
              <IconHeading icon={EnvelopeSimpleIcon} title="Where should we send the OTP?" sub="We'll email a 6-digit code valid for 10 minutes." />
              <div className="space-y-2">
                <Label className="overline text-brand-blue">Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  className="rounded-none h-11 border-slate-300 focus-visible:ring-0 focus-visible:border-brand-blue"
                  data-testid="forgot-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="w-full h-11 rounded-none bg-brand-orange hover:bg-brand-orange-ink text-white"
                data-testid="forgot-send-otp-btn"
              >
                {busy ? "Sending…" : "Send OTP"}
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={verifyOtp} className="space-y-5" data-testid="forgot-step-otp">
              <IconHeading icon={KeyIcon} title="Enter the 6-digit code" sub={`Sent to ${email}. The code expires in 10 minutes.`} />
              <div className="space-y-2">
                <Label className="overline text-brand-blue">OTP</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))}
                  required
                  autoFocus
                  className="rounded-none h-12 border-slate-300 text-center font-mono-num text-xl tracking-[0.5em] focus-visible:ring-0 focus-visible:border-brand-blue"
                  data-testid="forgot-otp-input"
                />
              </div>
              <Button
                type="submit"
                disabled={busy || otp.length < 4}
                className="w-full h-11 rounded-none bg-brand-orange hover:bg-brand-orange-ink text-white"
                data-testid="forgot-verify-otp-btn"
              >
                {busy ? "Verifying…" : "Verify OTP"}
              </Button>
              <div className="flex items-center justify-between text-xs text-slate-500 pt-1">
                <button
                  type="button"
                  onClick={() => { setStep(1); setOtp(""); }}
                  className="uppercase tracking-wider hover:text-brand-blue"
                  data-testid="forgot-change-email-btn"
                >
                  Change email
                </button>
                <button
                  type="button"
                  onClick={resendOtp}
                  disabled={busy}
                  className="uppercase tracking-wider hover:text-brand-blue disabled:opacity-50"
                  data-testid="forgot-resend-otp-btn"
                >
                  Resend OTP
                </button>
              </div>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={resetPassword} className="space-y-5" data-testid="forgot-step-password">
              <IconHeading icon={LockKeyIcon} title="Set a new password" sub="Pick a password you'll remember — at least 6 characters." />
              <div className="space-y-2">
                <Label className="overline text-brand-blue">New password</Label>
                <Input
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  required
                  minLength={6}
                  autoFocus
                  className="rounded-none h-11 border-slate-300 focus-visible:ring-0 focus-visible:border-brand-blue"
                  data-testid="forgot-newpw-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="overline text-brand-blue">Confirm new password</Label>
                <Input
                  type="password"
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  required
                  minLength={6}
                  className="rounded-none h-11 border-slate-300 focus-visible:ring-0 focus-visible:border-brand-blue"
                  data-testid="forgot-confirmpw-input"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="w-full h-11 rounded-none bg-brand-orange hover:bg-brand-orange-ink text-white"
                data-testid="forgot-reset-btn"
              >
                {busy ? "Updating…" : "Update password"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function StepStrip({ step }) {
  const labels = ["Email", "OTP", "New password"];
  return (
    <div className="flex items-center gap-2 mt-5">
      {labels.map((lbl, i) => {
        const n = i + 1;
        const active = n === step;
        const done = n < step;
        return (
          <div key={lbl} className="flex items-center flex-1">
            <div
              className={`h-6 w-6 flex items-center justify-center text-[11px] font-bold border ${
                done
                  ? "bg-brand-blue text-white border-brand-blue"
                  : active
                  ? "bg-brand-orange text-white border-brand-orange"
                  : "bg-white text-slate-400 border-slate-300"
              }`}
              data-testid={`forgot-step-indicator-${n}`}
            >
              {done ? <CheckCircleIcon size={14} weight="fill" /> : n}
            </div>
            <div className={`ml-2 overline whitespace-nowrap ${active ? "text-brand-blue" : done ? "text-slate-500" : "text-slate-400"}`} style={{ fontSize: 10 }}>
              {lbl}
            </div>
            {i < labels.length - 1 && <div className="flex-1 h-px bg-slate-200 mx-2" />}
          </div>
        );
      })}
    </div>
  );
}

function IconHeading({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-start gap-3">
      <div className="h-10 w-10 bg-brand-blue-tint flex items-center justify-center border border-brand-blue/20">
        <Icon size={20} className="text-brand-blue" weight="duotone" />
      </div>
      <div>
        <div className="font-display font-bold text-brand-blue">{title}</div>
        <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
      </div>
    </div>
  );
}
